// Step configs for the existing assignment-creation tour
const assignmentCreationSteps = [
  {
    id: 'assignment-button',
    targetId: 'assignment-button',
    title: 'Click Assignment',
    body: "Let's walk through how to create an assignment. Click Assignment.",
    advanceOn: 'click',
    placement: 'bottom',
    // On a refresh-resume the assignments page may not be the page the
    // user is on; bow out fast (1s) instead of waiting the default 5s.
    notFoundTimeoutMs: 1000,
  },
  {
    id: 'select-activity',
    targetId: 'select-activity',
    title: 'Select Activity',
    body: 'Choose an activity type for this assignment. For this tour, select Classification.',
    advanceOn: 'click',
    placement: 'bottom',
    clickTarget: '[data-tour-id="select-activity"] .button-game:first-child',
  },
  {
    id: 'general-info-panel',
    targetId: 'general-info-panel',
    title: 'Modify General Info',
    body: 'You can rename the assignment and set the submission deadline here, then click Save to continue.',
    advanceOn: 'manual',
    placement: 'left',
  },
  {
    id: 'add-level-button',
    targetId: 'add-level-button',
    title: 'Add a Level',
    body: 'The exercise starts simple with sorting the 5 Elements, but you\'d want to add a level to make it more challenging.',
    advanceOn: 'click',
    placement: 'bottom',
    searchDelay: 2000,
  },
  {
    id: 'level-filters-left',
    targetId: 'level-filters-left',
    title: 'Filter Options',
    body: 'On the left, filter by incorporated or non-incorporated businesses, and by decision type.',
    advanceOn: 'next',
    placement: 'right',
  },
  {
    id: 'level-accounts-right',
    targetId: 'level-accounts-right',
    title: 'Select Accounts',
    body: 'On the right, pick the accounts your students will review. Students can only make up to 15 decisions, so if you select more, the app will randomly choose 15 from your selection.',
    advanceOn: 'next',
    placement: 'left',
  },
  {
    id: 'level-save-button',
    targetId: 'level-save-button',
    title: 'Save the Level',
    body: 'Click Save to finish setting up this assignment.',
    advanceOn: 'click',
    placement: 'top',
  },
  {
    id: 'assignment-created',
    targetId: 'assignment-created',
    title: 'Assignment Created!',
    body: 'Congratulations! You\'ve created an assignment! To edit it, click the pencil icon (left). To test it, click the Start button (right).',
    advanceOn: 'close',
    placement: 'top',
    navigateTo: '/instructor/settings/assignments',
  },
];

// Step configs for the new tournament-overview tour
const tournamentOverviewSteps = [
  {
    id: 'competition-overview',
    targetId: 'competition-overview',
    popoverAnchorId: 'competition-overview-anchor',
    title: 'Competition Overview',
    body: (data) => {
      const roundCount = data?.tournament?.round_count ?? 4;
      const roundNameMap = { 4: 'Sweet 16', 3: 'Elite 8', 2: 'Final 4', 1: 'Championship' };
      const teamCountMap = { 4: 16, 3: 8, 2: 4, 1: 2 };
      const firstRoundName = roundNameMap[roundCount] || 'Sweet 16';
      const teamCount = teamCountMap[roundCount] ?? 16;
      return `This is your tournament bracket. You've set up a ${roundCount}-round tournament starting with the ${firstRoundName}, with students randomly assigned to ${teamCount} teams. The first game is Account Classification, teams will compete round by round until a champion is crowned.`;
    },
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'round-toggle',
    targetId: 'round-toggle',
    title: 'Open & Close the Round',
    body: 'Use this toggle to open or close the current round. Students can only submit decisions while the round is open.',
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'update-scores',
    targetId: 'update-scores-button',
    title: 'Update Scores',
    body: 'Click Update Scores to refresh team standings. We recommend updating scores while the round is in progress. Live score updates drive engagement and keep students invested in the competition.',
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'next-round',
    targetId: 'next-round-button',
    title: 'Navigate to the Next Round',
    body: 'When the current round is complete, click Next round to advance the tournament. Winning teams move forward and losing teams are assigned to the winning teams up until the Championship game.',
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'preview',
    targetId: 'preview-button',
    title: 'Preview the Round',
    body: 'Click Preview to provide students an overview of the round exercise before you open the round.',
    advanceOn: 'close',
    placement: 'bottom',
    isFinal: true,
  },
];

// Step configs for the student-side review-session tour
const studentTournamentOverviewSteps = [
  {
    id: 'competition-overview',
    targetId: 'competition-overview',
    popoverAnchorId: 'competition-overview-anchor',
    title: 'Competition Overview',
    body: (data) => {
      const roundCount = data?.tournament?.round_count ?? 4;
      const roundNameMap = { 4: 'Sweet 16', 3: 'Elite 8', 2: 'Final 4', 1: 'Championship' };
      const teamCountMap = { 4: 16, 3: 8, 2: 4, 1: 2 };
      const firstRoundName = roundNameMap[roundCount] || 'Sweet 16';
      const teamCount = teamCountMap[roundCount] ?? 16;
      const firstGameType = data?.firstGameType || 'Account Classification';
      return `This is the tournament bracket. There are ${roundCount} rounds in this tournament, starting with a total of ${teamCount} teams. You're starting in the ${firstRoundName} round and your first exercise is ${firstGameType}.`;
    },
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'user-team',
    targetId: 'user-team',
    title: 'Your Assigned Team',
    body: 'This is your assigned team. Decisions are made individually, but your scores are combined and displayed as a team. The Winning teams move on to the next round and losing teams join winning teams up until the Championship Round.',
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'scores-navigation',
    targetId: 'round-management',
    title: 'Scores & Navigation',
    body: 'This is where you would click to pull updated scores or to navigate to the next round.',
    advanceOn: 'next',
    placement: 'bottom',
  },
  {
    id: 'start-exercise',
    targetId: 'start-button',
    title: 'Start Exercise',
    body: 'This is where to access the round exercise. Click here when button is active. All the best!',
    advanceOn: 'close',
    placement: 'bottom',
    isFinal: true,
  },
];

// Post-level reflection tour for the `class-elements` scenario level.
// Steps 1 & 4 use noOverlay+'center' to render the popover at viewport
// center with no dim/spotlight — read like a follow-up note rather than a
// pointer at a specific UI element. Steps 2 & 3 highlight the Balance
// Sheet and Income Statement columns (tagged in Classification/index.js).
const classElementsReflectionSteps = [
  {
    id: 'class-elements-intro',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Nice work!',
    body: "You've just practiced something that sits at the very foundation of financial accounting: knowing which elements belong on which financial statement.",
    advanceOn: 'next',
  },
  {
    id: 'class-elements-balance-sheet',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'Balance Sheet',
    body: "Assets, Liabilities, and Equity tell the story of a company's balances at a single moment in time.",
    advanceOn: 'next',
  },
  {
    id: 'class-elements-income-statement',
    targetId: 'income-statement-column',
    placement: 'left',
    title: 'Income Statement',
    body: 'While Revenues and Expenses tell the story of how a company performed over a specific period of time from using the resources shown on its Balance Sheet.',
    advanceOn: 'next',
  },
  {
    id: 'class-elements-summary',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Summary',
    body: 'Each element answers a different question about the company, and knowing them helps you think like the investors, lenders, and managers who rely on this information.',
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `class-tricky` scenario level. Step 1 and 4 are
// centered with both columns highlighted; steps 2 and 3 each highlight
// only the Balance Sheet (the deferral / contra-account discussion lives
// on the Balance Sheet side), with the popover to the right.
const classTrickyReflectionSteps = [
  {
    id: 'class-tricky-intro',
    targetId: 'balance-sheet-column',
    placement: 'center',
    title: 'Nice work!',
    body: "You just tackled some of the trickiest accounts in financial accounting, and if they felt confusing, that's completely normal. Deferral and contra accounts can be confusing because their names and behavior often seem to contradict what you've learned about elements and normal balances.",
    advanceOn: 'next',
  },
  {
    id: 'class-tricky-deferrals',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'Deferrals',
    body: 'Take Deferred Expenses (i.e. Prepaid Expenses). The word "expense" is right in the name, but they are actually Assets (e.g. Prepaid), because the company paid cash in advance for a benefit it will receive later. Deferred Revenue is the flip side: it sounds like revenue, but it\'s a liability (e.g. Unearned Revenues), because the company received cash from customers in advance before earning it.',
    advanceOn: 'next',
  },
  {
    id: 'class-tricky-contra-accounts',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'Contra Accounts',
    body: 'These accounts add another layer because it carries an offsetting balance to whichever element it is associated with. For example, Accumulated Depreciation is linked to the Asset element, but it’s a Contra Asset Account, which means it reduces the value of the associated assets (e.g. Property & Equipment).',
    advanceOn: 'next',
  },
  {
    id: 'class-tricky-summary',
    targetId: 'balance-sheet-column',
    placement: 'center',
    title: 'Why It Matters',
    body: "These accounts exist so revenues and expenses are recorded in the period they actually relate to. This ties to 2 core principles you'll learn in your course: Revenue Recognition and Expense Recognition.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `classified-multistep` scenario level. Steps 1
// & 4 spotlight both Balance Sheet and Income Statement; step 2 only
// Balance Sheet; step 3 only Income Statement. Same column tag IDs as
// the other Classification reflection tours.
// Realco reflection tour. Five steps walk the student through a real
// company's Classified Balance Sheet + Multi-Step Income Statement.
// Steps 1-2 spotlight Balance Sheet, 3-4 spotlight Income Statement,
// 5 spotlights both. Title/body are functions that receive
// `{ companyName, salesRevenueBillions }` from startTour's data bag.
const realcoReflectionSteps = [
  {
    id: 'realco-accounting-equation',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'The Accounting Equation Always Balances',
    body: ({ companyName = '' } = {}) =>
      `Let's start with the balance sheet. As you can see, the accounting equation holds: Assets = Liabilities + Equity. Essentially, every dollar of ${companyName}'s total assets is financed either through borrowings (liabilities) or ownership's equity.`,
    advanceOn: 'next',
  },
  {
    id: 'realco-liabilities-equity-finance-assets',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'Liabilities & Equity Finance Assets',
    body: ({ companyName = '' } = {}) =>
      `Now that we know the equation balances, the next question is how ${companyName}'s assets are being financed. Investors review ${companyName}'s capital structure to evaluate how much of the company's assets are funded by debt versus equity, and importantly, whether short-term liabilities (current) are being used to finance long-term assets (noncurrent). Funding long-term assets with short-term debt creates the risk of a liquidity crisis, since repayment comes due before the cash flows from those assets arrive.`,
    advanceOn: 'next',
  },
  {
    id: 'realco-assets-generate-revenues',
    targetId: 'income-statement-column',
    placement: 'left',
    title: 'Assets Generate Revenues',
    body: ({ companyName = '', salesRevenueBillions = '' } = {}) =>
      `Once we understand how ${companyName}'s assets are financed, we can ask what those assets actually do for the company. In 2025, ${companyName} put their assets to work and produced $${salesRevenueBillions} billion in revenue.`,
    advanceOn: 'next',
  },
  {
    id: 'realco-turning-revenues-to-profits',
    targetId: 'income-statement-column',
    placement: 'left',
    title: 'Turning Revenues to Profits',
    // Body is an array → each entry renders as its own paragraph so the
    // bullet items below the intro read as a tidy list, not one block.
    // Paragraphs with HTML are honored by TourPopover (inline <b>).
    body: ({ companyName = '' } = {}) => [
      `Beyond revenues, investors would break down ${companyName}'s profitability to see where the company actually makes (or loses) money, because not all income and expenses are created equal.`,
      `<b>Gross Profit:</b> How good is ${companyName} at selling its products after production costs?`,
      `<b>Operating Profit:</b> How well does ${companyName} run the business after overhead like salaries, rent, and R&D?`,
      `<b>Non-Operating Items:</b> What one-time gains or expenses could mislead investors about ${companyName}'s recurring profitability?`,
    ],
    advanceOn: 'next',
  },
  {
    id: 'realco-big-picture',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'The Big Picture',
    body:
      "As you see, financials aren't isolated reports, they're a connected story of how a company is funded, how it deploys those funds, and how well it turns them into lasting profit.",
    advanceOn: 'close',
    isFinal: true,
  },
];

const classifiedMultistepReflectionSteps = [
  {
    id: 'classified-multistep-intro',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Well done!',
    body: "You've moved beyond simply identifying accounts and their elements to classifying them by their nature and timing.",
    advanceOn: 'next',
  },
  {
    id: 'classified-multistep-current-noncurrent',
    targetId: 'balance-sheet-column',
    placement: 'right',
    title: 'Current vs. Non-Current',
    body: 'The Balance Sheet is segmented by current versus non-current to help readers of financials make informed decisions. For example, a short term lender will focus on a company’s current assets and liabilities to assess liquidity, while a long-term investor will look at the company’s long term assets and liabilities.',
    advanceOn: 'next',
  },
  {
    id: 'classified-multistep-operating-nonoperating',
    targetId: 'income-statement-column',
    placement: 'left',
    title: 'Operating vs. Non-Operating',
    body: 'The Income Statement is segmented into 3 sections to help readers of financials better understand where profitability is coming from. For example, 2 companies might report the same Net Income, but one could have earned it from selling products (gross profit section) while the other could have earned it from a one-time equipment sale (non-operating section).',
    advanceOn: 'next',
  },
  {
    id: 'classified-multistep-summary',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Skills That Matter!',
    body: 'Each section of the Classified and Multi-step format tells a different part of the company’s story. Getting comfortable is what allows you to read an income statement the way an analyst does, as a layered explanation of how a business actually operates and makes money.',
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `class-default` scenario level. Each step is
// centered between the columns; targetIds spotlight both Balance Sheet
// and Income Statement throughout, dimming the rest of the board.
const classDefaultReflectionSteps = [
  {
    id: 'class-default-intro',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Great job!',
    body: "You've just worked through one of the most important classification skills in accounting: identifying whether a specific account represents an Asset, Liability, Equity, Revenue, or Expense.",
    advanceOn: 'next',
  },
  {
    id: 'class-default-real-world',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'Theory vs. Reality',
    body: 'While the 5 elements may seem straightforward in theory, real businesses use hundreds of account names, and recognizing which element each one belongs to is what separates memorization from true understanding.',
    advanceOn: 'next',
  },
  {
    id: 'class-default-skill',
    targetIds: ['balance-sheet-column', 'income-statement-column'],
    placement: 'center',
    title: 'The Skill That Matters',
    body: "Strong accountants don't memorize every account name, they reason from what the account represents and connect it back to its underlying element, which is exactly what you just practiced. This skill matters the moment you start recording and analyzing transactions.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `norm-elements` scenario level (Normal Balance
// game). Steps 1 & 4 spotlight both debit and credit columns; step 2
// highlights debit only, step 3 highlights credit only. Side popovers
// (steps 2/3) top-align with the column header via `defaultAlign: 'start'`.
const normElementsReflectionSteps = [
  {
    id: 'norm-elements-intro',
    targetIds: ['debit-column', 'credit-column'],
    placement: 'center',
    title: 'Good job!',
    body: "You've just sorted each of the five elements to its normal balance, either debit or credit. The \"normal balance\" is simply the side where an element naturally increases, and it's one of the most important patterns to lock in early.",
    advanceOn: 'next',
  },
  {
    id: 'norm-elements-debit',
    targetId: 'debit-column',
    placement: 'right',
    title: 'Debit Balances',
    body: 'Assets and Expenses carry normal debit balances. For example, a debit to assets = an increase to assets.',
    advanceOn: 'next',
  },
  {
    id: 'norm-elements-credit',
    targetId: 'credit-column',
    placement: 'left',
    title: 'Credit Balances',
    body: 'While Liabilities, Equity, and Revenues carry normal credit balances. For example, a credit to liabilities = an increase to liabilities.',
    advanceOn: 'next',
  },
  {
    id: 'norm-elements-summary',
    targetIds: ['debit-column', 'credit-column'],
    placement: 'center',
    title: 'The Accounting Equation',
    body: "This pattern comes directly from the Accounting Equation: Assets = Liabilities + Equity. It's the framework companies use to keep their books in order, and it reflects a simple idea: whatever a company owns has to be paid for somehow, either through debt (liabilities) or equity.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `norm-default` scenario level (Normal Balance
// game). Three centered steps with both Debit and Credit columns
// highlighted throughout — same shape as class-default-reflection.
const normDefaultReflectionSteps = [
  {
    id: 'norm-default-intro',
    targetIds: ['debit-column', 'credit-column'],
    placement: 'center',
    title: 'Boom!',
    body: "You've moved from knowing the normal balance of each element to applying that knowledge at the account level.",
    advanceOn: 'next',
  },
  {
    id: 'norm-default-elements-to-accounts',
    targetIds: ['debit-column', 'credit-column'],
    placement: 'center',
    title: 'From Elements to Accounts',
    body: "You're going to be introduced to a lot of accounts this semester. This is where the previous exercise pays off: once you know the normal balance of each element, you automatically know the normal balance of every account that rolls up into it. For example, if assets carry a normal debit balance, then Cash, and every other asset account follow the same rule. You don't need to memorize each account individually, you just need to recognize which element it belongs to.",
    advanceOn: 'next',
  },
  {
    id: 'norm-default-contra-accounts',
    targetIds: ['debit-column', 'credit-column'],
    placement: 'center',
    title: 'Contra Accounts',
    body: 'One key exception worth knowing is Contra Accounts, which intentionally behave the opposite of the element they\'re tied to. A good example is Accumulated Depreciation, it lives within the Asset element, but it carries a credit balance because its job is to reduce the value of related assets like Property & Equipment. Watch out for these "contra-dictory" accounts ;)',
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `increase-decrease` scenario level (Balanced
// Equation game). Different layout from Classification/Normal Balance:
// - `be-balance-sheet` is the Balance Sheet panel on the right.
// - `be-equation-box` is the TransactionRecords panel (Assets - Liabilities
//   = Equity, including the drag/drop section) on the left.
const increaseDecreaseReflectionSteps = [
  {
    id: 'increase-decrease-balance-sheet',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'Great job!',
    body: 'You just worked through an important first step in analyzing any transaction: deciding how a transaction impacts the Balance Sheet elements: Assets, Liabilities, Equity.',
    advanceOn: 'next',
  },
  {
    id: 'increase-decrease-equation-box',
    targetId: 'be-equation-box',
    placement: 'right',
    title: 'The Accounting Equation',
    body: 'Every single journal transaction ultimately impacts the accounting equation with a resulting equation that’s always in balance. For example, if a company takes out a loan, assets go up (cash received) and liabilities go up (loan owed).',
    advanceOn: 'next',
  },
  {
    id: 'increase-decrease-systems-thinking',
    targetIds: ['be-equation-box', 'be-balance-sheet'],
    placement: 'top',
    title: 'Systems Thinking',
    body: "Thinking about transactions this way, as part of a connected system, helps you reason through their impact on a company's financial position and decide how each one should be recorded.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `debit-credit` scenario level (Balanced
// Equation game). Step 1 highlights only the equation box; steps 2 and
// 3 highlight both the equation box and the Balance Sheet panel. Every
// step uses placement 'top' so the popover sits directly above the
// equation box (over the transaction text area at the box's top).
const debitCreditReflectionSteps = [
  {
    id: 'debit-credit-intro',
    targetId: 'be-equation-box',
    placement: 'top',
    title: 'Awesome work!',
    body: "You've now layered on the next big skill: translating those increases and decreases into debits and credits. This is where the Accounting Equation meets the double-entry accounting system.",
    advanceOn: 'next',
  },
  {
    id: 'debit-credit-connection',
    targetIds: ['be-equation-box', 'be-balance-sheet'],
    placement: 'top',
    title: 'The Connection',
    body: "Here's the connection: each element has a normal balance side based on where it sits in the Accounting Equation. Assets sit on the left, so they're increased with debits. Liabilities and equity sit on the right, so they're increased with credits.",
    advanceOn: 'next',
  },
  {
    id: 'debit-credit-double-entry',
    targetIds: ['be-equation-box', 'be-balance-sheet'],
    placement: 'top',
    title: 'Double-Entry Accounting',
    body: "Once you know which way an element is moving, you automatically know whether to debit or credit it. The fact that every transaction has equal debits and credits is exactly what keeps the Accounting Equation in balance that's the core of double-entry accounting, and you just put it into practice.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `accounts-impacted` scenario level (Balanced
// Equation game). Steps 1 & 2 highlight only the Balance Sheet (popover
// to its left); step 3 highlights both the equation box and the Balance
// Sheet, with the popover anchored above the equation box (matches the
// `increase-decrease-reflection` final-step shape).
const accountsImpactedReflectionSteps = [
  {
    id: 'accounts-impacted-intro',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'Excellent work!',
    body: "You've now zoomed in from the element level to the account level, pinpointing the exact accounts affected by each transaction and applying the right debit or credit. This is the level where journal entries actually get written, and it's a skill you'll use constantly throughout your accounting course.",
    advanceOn: 'next',
  },
  {
    id: 'accounts-impacted-detail',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'Account-Level Detail',
    body: 'The reason this step matters is that real businesses don\'t track activity through numerous accounts. For example, when a company collects cash from a customer, it\'s not just "assets up"; it\'s specifically Cash going up and Accounts Receivable going down. That detail is what makes financial statements useful.',
    advanceOn: 'next',
  },
  {
    id: 'accounts-impacted-summary',
    targetIds: ['be-equation-box', 'be-balance-sheet'],
    placement: 'top',
    title: 'What Accountants Do',
    body: "The good news is that you don't need to memorize each account. You just need to know the element it belongs to and if the account should increase or decrease. You're understanding what accountants do every day: turning real-world business events into precise, structured records.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `income-statement-impact` scenario level
// (Balanced Equation game). All three steps highlight only the Balance
// Sheet panel; popover sits to its left, top-aligned with the header.
const incomeStatementImpactReflectionSteps = [
  {
    id: 'income-statement-impact-intro',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'Good job!',
    body: "Hopefully, you’re now seeing how Income Statement activity ultimately flows back into the Balance Sheet. Many students think of these two statements as unrelated, but they're deeply linked, and you just saw that link in action.",
    advanceOn: 'next',
  },
  {
    id: 'income-statement-impact-flow',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'How It Flows',
    body: "Here's how it works: When a company earns revenue and incurs expenses in a period, the net income that results from that gets rolled up into equity either through the Retained Earnings account (if the business is a corporation) or Owner’s Capital account (for non-corporate businesses).",
    advanceOn: 'next',
  },
  {
    id: 'income-statement-impact-summary',
    targetId: 'be-balance-sheet',
    placement: 'left',
    title: 'One Connected System',
    body: "And through it all, the Accounting Equation (Assets = Liabilities + Equity) stays in balance, because every Income Statement transaction has a matching effect on the Balance Sheet. This is what makes the financial statements work as one connected system, and it's a big step toward thinking like a financial professional.",
    advanceOn: 'close',
    isFinal: true,
  },
];

// Reflection tour for the `assumptions-principles-constraints` level
// (The Principles game). Step 1 and 5 highlight all three category
// boxes with the popover anchored to the central Select All card.
// Steps 2-4 each highlight a single category with the popover sitting
// next to it (placement chosen to point inward toward the card).
const apcReflectionSteps = [
  {
    id: 'apc-intro',
    targetIds: ['apc-assumptions', 'apc-principles', 'apc-constraints'],
    popoverAnchorId: 'apc-select-all-card',
    placement: 'center',
    title: 'Nice work!',
    body: "Let's connect the dots on what you just practiced. Assumptions, Principles, and Constraints form the foundation of financial accounting. Nail these concepts now and everything that comes next in your course gets easier.",
    advanceOn: 'next',
  },
  {
    id: 'apc-assumptions',
    targetId: 'apc-assumptions',
    placement: 'right',
    title: 'Assumptions',
    body: "Assumptions are the basic conditions we accept as true about a business. Without them, you can't report on a business without first agreeing on what it is, how to measure it, and whether it'll still exist next year.",
    advanceOn: 'next',
  },
  {
    id: 'apc-principles',
    targetId: 'apc-principles',
    placement: 'top',
    title: 'Principles',
    body: "Principles are the guidelines accountants follow to record activity accurately. They ensure consistency, so investors, lenders, and managers can trust and compare what they're reading.",
    advanceOn: 'next',
  },
  {
    id: 'apc-constraints',
    targetId: 'apc-constraints',
    placement: 'left',
    title: 'Constraints',
    body: 'Constraints are the real-world limits on how strictly we apply the principles. They keep businesses from wasting resources on trivial precision while protecting users from overly optimistic reporting.',
    advanceOn: 'next',
  },
  {
    id: 'apc-summary',
    targetIds: ['apc-assumptions', 'apc-principles', 'apc-constraints'],
    popoverAnchorId: 'apc-select-all-card',
    placement: 'center',
    title: 'How It All Ties Together',
    body: 'Think of it like building a house. Assumptions are the foundation, the ground you build on. Principles are the blueprints, the rules for how everything gets constructed. Constraints are the building codes, the practical limits that keep things safe, honest, and useful.',
    advanceOn: 'close',
    isFinal: true,
  },
];

// Tour registry — add more tours here as needed. Each tour is identified
// by a stable name so it can be started independently via startTour(name).
export const tours = {
  'assignment-creation': {
    name: 'assignment-creation',
    steps: assignmentCreationSteps,
    hideBack: true,
  },
  'tournament-overview': {
    name: 'tournament-overview',
    steps: tournamentOverviewSteps,
    hideBack: true,
    readOnly: true,
  },
  'student-tournament-overview': {
    name: 'student-tournament-overview',
    steps: studentTournamentOverviewSteps,
    hideBack: true,
    readOnly: true,
  },
  'class-elements-reflection': {
    name: 'class-elements-reflection',
    steps: classElementsReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Top-align side popovers with the highlighted column header. Used
    // by all Classification/Normal Balance reflection tours since their
    // level layouts share the same column structure.
    defaultAlign: 'start',
  },
  'class-default-reflection': {
    name: 'class-default-reflection',
    steps: classDefaultReflectionSteps,
    hideBack: false,
    readOnly: true,
  },
  'class-tricky-reflection': {
    name: 'class-tricky-reflection',
    steps: classTrickyReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Side popovers (steps 2 and 3) top-align with the Balance Sheet
    // column header, matching the other Classification reflection tours.
    defaultAlign: 'start',
  },
  'classified-multistep-reflection': {
    name: 'classified-multistep-reflection',
    steps: classifiedMultistepReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Steps 2 and 3 highlight a single column with a side popover —
    // top-align with the column header, same as the other Classification
    // reflection tours.
    defaultAlign: 'start',
  },
  'realco-reflection': {
    name: 'realco-reflection',
    steps: realcoReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Side popovers (steps 1-4) top-align with the spotlit column header.
    defaultAlign: 'start',
    // 40% wider than the default 340px to fit the reflection copy.
    popoverWidth: 476,
  },
  'norm-elements-reflection': {
    name: 'norm-elements-reflection',
    steps: normElementsReflectionSteps,
    hideBack: false,
    readOnly: true,
    defaultAlign: 'start',
  },
  'norm-default-reflection': {
    name: 'norm-default-reflection',
    steps: normDefaultReflectionSteps,
    hideBack: false,
    readOnly: true,
  },
  'increase-decrease-reflection': {
    name: 'increase-decrease-reflection',
    steps: increaseDecreaseReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Side popovers (steps 1 and 2) top-align with their target's header
    // for a consistent visual rhythm with the rest of the reflection tours.
    defaultAlign: 'start',
  },
  'debit-credit-reflection': {
    name: 'debit-credit-reflection',
    steps: debitCreditReflectionSteps,
    hideBack: false,
    readOnly: true,
  },
  'accounts-impacted-reflection': {
    name: 'accounts-impacted-reflection',
    steps: accountsImpactedReflectionSteps,
    hideBack: false,
    readOnly: true,
    // Side popovers (steps 1 and 2) top-align with the Balance Sheet
    // header so they sit level with the highlighted target.
    defaultAlign: 'start',
  },
  'income-statement-impact-reflection': {
    name: 'income-statement-impact-reflection',
    steps: incomeStatementImpactReflectionSteps,
    hideBack: false,
    readOnly: true,
    defaultAlign: 'start',
  },
  'apc-reflection': {
    name: 'apc-reflection',
    steps: apcReflectionSteps,
    hideBack: false,
    readOnly: true,
  },
};

export const DEFAULT_TOUR_NAME = 'assignment-creation';

export function getTour(name) {
  return tours[name] || null;
}
